/**
 * Fase 4 Test Component
 * 
 * Test component for Visual Animations & Cell Highlighting
 * Demonstrates LiveRackGrid with simulated activities
 * 
 * @author BizClick WMS
 * @version 1.25 - Fase 4 Implementation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Zap, AlertCircle } from 'lucide-react';
import { LiveRackGrid } from './LiveRackGrid';
import { RackStructure, BinLocation } from '../rack-builder/types';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';

/**
 * Generate a mock rack structure for testing
 */
const generateMockRack = (rackNumber: number = 1): RackStructure => {
  const verticals = ['1', '2', '3', '4', '5'];
  const shelves = ['0', '1', '2', '3', '4', '5'];
  const locations: BinLocation[] = [];
  const rackNumberStr = rackNumber.toString().padStart(2, '0');

  verticals.forEach(vertical => {
    shelves.forEach(shelf => {
      const binCode = `R${rackNumberStr}-V${vertical.padStart(2, '0')}-H${shelf.padStart(2, '0')}`;

      locations.push({
        binCode,
        vertical,
        shelf,
        rackNumber: rackNumberStr, // String type for BinLocation
        description1: `Test lokasjon ${binCode}`,
        binSizeNo: Math.random() > 0.5 ? 1 : 2,
        binTypeNo: Math.random() > 0.5 ? 1 : 2,
        pickableYesNo: true,
        pickPriorityNo: Math.floor(Math.random() * 100),
        itemNo: Math.random() > 0.7 ? `ITEM${Math.floor(Math.random() * 1000)}` : undefined,
        resourceNo: 1, // Number type for BinLocation
        stcZoneID: 'ZONE-A'
      });
    });
  });

  return {
    rackNumber: rackNumberStr, // String type for RackStructure
    verticals,
    shelves,
    locations,
    gridWidth: verticals.length,
    gridHeight: shelves.length
  };
};

/**
 * Generate random activity for testing
 */
const generateRandomActivity = (locations: BinLocation[]): LocationActivityEvent => {
  const randomLocation = locations[Math.floor(Math.random() * locations.length)];
  const activityTypes = [
    ActivityType.Pick,
    ActivityType.PutAway,
    ActivityType.Transfer,
    ActivityType.CorrectionOut,
    ActivityType.CorrectionIn,
    ActivityType.Counting,
    ActivityType.Receiving,
    ActivityType.Reservation
  ];
  const randomType = activityTypes[Math.floor(Math.random() * activityTypes.length)];

  return {
    binCode: randomLocation.binCode,
    rackNumber: parseInt(randomLocation.rackNumber, 10), // Number type for LocationActivityEvent
    activityType: randomType,
    timestamp: new Date().toISOString(),
    operatorId: `OP${Math.floor(Math.random() * 10) + 1}`,
    itemNo: `ITEM${Math.floor(Math.random() * 1000)}`,
    quantity: Math.floor(Math.random() * 50) + 1,
    companyNo: '1',
    customerNo: 'TEST',
    warehouseId: 'WH001'
  };
};

export const Fase4Test: React.FC = () => {
  const [rackStructure] = useState<RackStructure>(() => generateMockRack(1));
  const [activeActivities, setActiveActivities] = useState<Map<string, LocationActivityEvent[]>>(new Map());
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState<'slow' | 'normal' | 'fast'>('normal');
  const [activityLog, setActivityLog] = useState<LocationActivityEvent[]>([]);
  const [stats, setStats] = useState({
    totalActivities: 0,
    activeLocations: 0,
    completedAnimations: 0
  });

  /**
   * Add a new random activity
   */
  const addRandomActivity = useCallback(() => {
    const newActivity = generateRandomActivity(rackStructure.locations);
    
    setActiveActivities(prev => {
      const updated = new Map(prev);
      const existing = updated.get(newActivity.binCode) || [];
      updated.set(newActivity.binCode, [...existing, newActivity]);
      return updated;
    });

    setActivityLog(prev => [newActivity, ...prev].slice(0, 20));
    
    setStats(prev => ({
      ...prev,
      totalActivities: prev.totalActivities + 1,
      activeLocations: new Set([...Array.from(activeActivities.keys()), newActivity.binCode]).size
    }));
  }, [rackStructure.locations, activeActivities]);

  /**
   * Clear all activities
   */
  const clearActivities = useCallback(() => {
    setActiveActivities(new Map());
    setActivityLog([]);
    setStats({
      totalActivities: 0,
      activeLocations: 0,
      completedAnimations: stats.completedAnimations
    });
  }, [stats.completedAnimations]);

  /**
   * Handle activity animation completion
   */
  const handleActivityComplete = useCallback((binCode: string, event: LocationActivityEvent) => {
    setStats(prev => ({
      ...prev,
      completedAnimations: prev.completedAnimations + 1
    }));

    // Remove completed activity after animation
    setActiveActivities(prev => {
      const updated = new Map(prev);
      const existing = updated.get(binCode) || [];
      const filtered = existing.filter(
        e => !(e.timestamp === event.timestamp && e.activityType === event.activityType)
      );
      
      if (filtered.length > 0) {
        updated.set(binCode, filtered);
      } else {
        updated.delete(binCode);
      }
      
      // Update active locations count
      setStats(s => ({
        ...s,
        activeLocations: updated.size
      }));
      
      return updated;
    });
  }, []);

  /**
   * Auto-generate activities at interval
   */
  useEffect(() => {
    if (!isAutoGenerating) return;

    const interval = setInterval(() => {
      addRandomActivity();
    }, 2000); // New activity every 2 seconds

    return () => clearInterval(interval);
  }, [isAutoGenerating, addRandomActivity]);

  /**
   * Get activity type name for display
   */
  const getActivityTypeName = (type: ActivityType): string => {
    const names: Record<ActivityType, string> = {
      [ActivityType.Pick]: 'Plukk',
      [ActivityType.PutAway]: 'Plassering',
      [ActivityType.Transfer]: 'Overføring',
      [ActivityType.CorrectionOut]: 'Korr. Ut',
      [ActivityType.CorrectionIn]: 'Korr. Inn',
      [ActivityType.Counting]: 'Telling',
      [ActivityType.Receiving]: 'Mottak',
      [ActivityType.Reservation]: 'Reservasjon',
      [ActivityType.StockTransfer]: 'Lageroverføring',
      [ActivityType.Replenishment]: 'Påfylling',
      [ActivityType.Optimization]: 'Optimalisering',
      [ActivityType.InternalMove]: 'Internflytt',
      [ActivityType.OpeningBalance]: 'Inngående saldo'
    };
    return names[type] || 'Ukjent';
  };

  /**
   * Get activity type color for display
   */
  const getActivityTypeColor = (type: ActivityType): string => {
    const colors: Record<ActivityType, string> = {
      [ActivityType.Pick]: 'text-orange-600 bg-orange-50',
      [ActivityType.PutAway]: 'text-green-600 bg-green-50',
      [ActivityType.Transfer]: 'text-blue-600 bg-blue-50',
      [ActivityType.CorrectionOut]: 'text-red-600 bg-red-50',
      [ActivityType.CorrectionIn]: 'text-green-600 bg-green-50',
      [ActivityType.Counting]: 'text-purple-600 bg-purple-50',
      [ActivityType.Receiving]: 'text-green-600 bg-green-50',
      [ActivityType.Reservation]: 'text-yellow-600 bg-yellow-50',
      [ActivityType.StockTransfer]: 'text-cyan-600 bg-cyan-50',
      [ActivityType.Replenishment]: 'text-green-600 bg-green-50',
      [ActivityType.Optimization]: 'text-yellow-600 bg-yellow-50',
      [ActivityType.InternalMove]: 'text-blue-600 bg-blue-50',
      [ActivityType.OpeningBalance]: 'text-gray-600 bg-gray-50'
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="h-screen flex flex-col p-6 bg-gray-50">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              Fase 4: Visual Animations Test
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Test av live aktivitets-animasjoner med pulsering og fade-out
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
              <div className="text-xs text-gray-500">Totalt aktiviteter</div>
              <div className="text-2xl font-bold text-gray-800">{stats.totalActivities}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
              <div className="text-xs text-gray-500">Aktive lokasjoner</div>
              <div className="text-2xl font-bold text-blue-600">{stats.activeLocations}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
              <div className="text-xs text-gray-500">Fullførte animasjoner</div>
              <div className="text-2xl font-bold text-green-600">{stats.completedAnimations}</div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={addRandomActivity}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Zap className="w-4 h-4" />
            Generer aktivitet
          </button>

          <button
            onClick={() => setIsAutoGenerating(!isAutoGenerating)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isAutoGenerating
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isAutoGenerating ? (
              <>
                <Pause className="w-4 h-4" />
                Stopp auto
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Start auto
              </>
            )}
          </button>

          <button
            onClick={clearActivities}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Nullstill
          </button>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-600">Hastighet:</span>
            <select
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(e.target.value as 'slow' | 'normal' | 'fast')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="slow">Langsom (7s)</option>
              <option value="normal">Normal (5s)</option>
              <option value="fast">Rask (3s)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main content - Grid and Activity Log */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Live Rack Grid */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4 overflow-hidden">
          <LiveRackGrid
            rackStructure={rackStructure}
            activeActivities={activeActivities}
            enableTransition={true}
            animationSpeed={animationSpeed}
            onActivityComplete={handleActivityComplete}
          />
        </div>

        {/* Activity Log Sidebar */}
        <div className="w-96 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Aktivitetslogg
          </h3>
          
          <div className="flex-1 overflow-y-auto space-y-2">
            {activityLog.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                Ingen aktiviteter ennå
              </div>
            ) : (
              activityLog.map((activity, index) => (
                <div
                  key={`${activity.binCode}-${activity.timestamp}-${index}`}
                  className={`p-3 rounded-lg border ${getActivityTypeColor(activity.activityType)}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-semibold text-sm">
                      {activity.binCode}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-white border">
                      {getActivityTypeName(activity.activityType)}
                    </span>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div>Vare: {activity.itemNo}</div>
                    <div>Antall: {activity.quantity} stk</div>
                    <div>Operatør: {activity.operatorId}</div>
                    <div className="text-gray-500">
                      {new Date(activity.timestamp).toLocaleTimeString('nb-NO')}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <strong>Tips:</strong> Klikk "Generer aktivitet" for å teste enkeltanimationer, 
          eller "Start auto" for kontinuerlig simulering. Hver aktivitet pulser i 5 sekunder 
          (eller justert hastighet) før den fader ut over 2 sekunder.
        </p>
      </div>
    </div>
  );
};

export default Fase4Test;
